import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Star } from "lucide-react";
import { formatEUR } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import StockBar from "@/components/StockBar";
import Stars from "@/components/Stars";
import { getReviews } from "@/lib/reviews";

const BADGE_STYLES = {
  "Stock Critique": "bg-red-600 text-white",
  "Édition Maudite": "bg-purple-700 text-white",
  "Promo Halloween": "bg-amber-500 text-[#08060B]",
};

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const discount = Math.round((1 - product.price / product.original_price) * 100);
  const reviews = getReviews(product.slug);

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if ((product.sizes && product.sizes.length > 0) || (product.variants && product.variants.length > 0)) {
      navigate(`/produit/${product.slug}`);
      return;
    }
    addItem(product, null, 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/produit/${product.slug}`} className="block gothic-card rounded-2xl overflow-hidden h-full flex flex-col">
        <div className="relative overflow-hidden aspect-square bg-[#0b0810]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08060B] via-transparent to-transparent opacity-70" />
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {product.bestseller && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wide bg-gradient-to-r from-yellow-400 to-amber-500 text-black inline-flex items-center gap-1 shadow-lg">
                <Star className="h-3 w-3 fill-black" /> Best-seller
              </span>
            )}
            {product.badge && (
              <span
                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wide ${
                  BADGE_STYLES[product.badge] || "bg-amber-500 text-black"
                }`}
              >
                {product.badge}
              </span>
            )}
          </div>
          {discount > 0 && (
            <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-black/70 text-amber-400 font-mono-tech border border-amber-500/40">
              -{discount}%
            </span>
          )}
        </div>

        <div className="p-3 sm:p-5 flex flex-col flex-1">
          <p className="eyebrow mb-1.5">{product.category}</p>
          <h3 className="font-display text-base sm:text-lg font-bold text-stone-100 leading-tight mb-2 group-hover:text-amber-400 transition-colors">
            {product.name}
          </h3>
          <div className="flex items-center gap-1.5 mb-2">
            <Stars value={reviews.rating} size={13} />
            <span className="text-xs text-stone-500 font-mono-tech">{reviews.rating.toFixed(1)} ({reviews.count})</span>
          </div>
          <p className="text-xs sm:text-sm text-stone-400 leading-relaxed line-clamp-2 mb-3 sm:mb-4 flex-1">
            {product.description}
          </p>

          <StockBar soldPct={product.sold_pct} stock={product.stock} className="mb-4" />

          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-baseline gap-1.5 sm:gap-2 min-w-0">
              {product.variants && product.variants.length > 0 && (
                <span className="text-xs text-stone-400">dès</span>
              )}
              <span className="font-mono-tech text-xl sm:text-2xl font-extrabold text-amber-400">
                {formatEUR(product.price)}
              </span>
              {product.original_price > product.price && (
                <span className="hidden sm:inline text-sm text-stone-500 line-through font-mono-tech">
                  {formatEUR(product.original_price)}
                </span>
              )}
            </div>
            <button
              onClick={quickAdd}
              className="btn-ember rounded-xl h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center shrink-0"
              data-testid={`add-to-cart-${product.slug}`}
              aria-label="Ajouter au panier"
            >
              <ShoppingCart className="h-5 w-5" />
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
